import { useTranslation } from '@hooks/useTranslation';
import React, { FC, useMemo, useState } from 'react';
import s from './inputsBlock.module.scss';
import { api } from '@api';
import { countryList, isAlphanumeric } from '@helpers/helpers';
import { profile } from '@specs/specs';
// import { useRouter } from 'next/router'
import { SocialManagerView } from '@specs/views';
import { artistSocialTypes } from '@specs/types';
import { Button } from '@components/UIKit/Button/Button';
import { Input } from '@components/UIKit/Input/Input';
import { Select } from '@components/UIKit/Select/Select';
import { Divider } from '@components/UIKit/Divider/Divider';
import { SpriteIcon } from '@components/Sprites/SpriteIcon';
import { useShowToast } from '@hooks/useShowToast';
import { useUpdateBio } from '@services/user/hooks/useUpdateBio';
import { Info } from '../Info/Info';
import dynamic from 'next/dynamic';
import { LinksEnum } from '@specs/navigation';
import { Textarea } from '@components/UIKit/Textarea/Textarea';
import { useRouter } from 'next/router';
const Modal = dynamic(import('@components/Modals/Modal').then(module => module.Modal), {
  ssr: false
});
const DeleteAccount = dynamic(import('@components/Modals/Variants/DeleteAccount/DeleteAccount').then(module => module.DeleteAccount), {
  ssr: false
});
interface InputsBlockLoadedProps {
  profile: profile;
  artistProfile: SocialManagerView.publicProfile['artistProfile'] | undefined;
  isMobile: boolean;
}
export interface ISettingsState {
  shortId: string;
  name: string;
  surname: string;
  email: string;
  countrycode: string | undefined;
  description: string;
}
const minLength = 5;
const maxLength = 20;
export const InputsBlockLoaded: FC<InputsBlockLoadedProps> = ({
  profile,
  artistProfile,
  isMobile
}) => {
  const {
    showToast
  } = useShowToast();
  const {
    translate: tr
  } = useTranslation();
  const [error, setError] = useState<{
    show: boolean;
    text: string;
  } | undefined>(undefined);
  const utils = api.useUtils();
  const router = useRouter();
  const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const deleteAccountHandler = async () => {
    window.open(LinksEnum.SUPPORT, '_blank', 'noreferrer');
  };
  const openDeleteAccountModal = () => {
    setDeleteAccountModalOpen(true);
  };
  const closeDeleteAccountModal = () => {
    setDeleteAccountModalOpen(false);
  };
  const socials = useMemo(() => artistProfile?.socials as Record<artistSocialTypes, string> | undefined, [artistProfile]);
  const defaultState = useMemo(() => ({
    shortId: profile.shortid,
    name: profile.name ?? '',
    surname: profile.surname ?? '',
    email: profile.email ?? '',
    countrycode: profile.countrycode ?? undefined,
    description: profile.description ?? ''
  }), [profile, artistProfile, socials]);
  const [settingState, setSettingsState] = useState<ISettingsState>(defaultState);
  const {
    name,
    surname,
    email,
    countrycode,
    shortId,
    description
  } = settingState;
  const {
    mutateAsync: updateShortId
  } = api.SocialManager.updateShortId.useMutation({
    mutationKey: ['SocialManager.updateShortId', profile.shortid],
    onSuccess: data => {
      if (data.status === 'reject') {
        showToast({
          variant: 'error',
          text: data.code
        });
        setError({
          show: true,
          text: tr({
            id: data.code
          })
        });
      } else if (data.status === 'success') {
        utils.invalidate();
      }
    },
    onError: () => {
      showToast({
        variant: 'error',
        text: 'error.default'
      });
    }
  });
  const {
    mutateAsync: updateBio
  } = useUpdateBio();
  const onChange = (name: string, value: string) => {
    if (name === 'shortId') {
      if (value.trim().length < minLength) {
        setError({
          show: true,
          text: tr({
            id: 'main.profiles.shortId.length.min'
          })
        });
      } else if (value.trim().length > maxLength) {
        setError({
          show: true,
          text: tr({
            id: 'main.profiles.shortId.length.max'
          })
        });
      } else if (!isAlphanumeric(value)) {
        setError({
          show: true,
          text: tr({
            id: 'main.profiles.shortId.filter'
          })
        });
      } else {
        setError(undefined);
      }
    }
    setSettingsState(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const setDefaultState = () => {
    setSettingsState(defaultState);
    setError(undefined);
  };
  const saveHandler = async () => {
    const profileShortID = profile.shortid;
    if (IS_PROFILE_CHANGED) {
      await updateBio({
        data: {
          email,
          countrycode,
          surname,
          name,
          description
        }
      });
    }
    if (profileShortID !== shortId) {
      const data = await updateShortId({
        newId: shortId
      });
      if (data.status === 'success') {
        await router.push({
          pathname: LinksEnum.USER,
          query: {
            slug: shortId.toLowerCase()
          }
        });
      }
    } else {
      await router.push({
        pathname: LinksEnum.USER,
        query: {
          slug: profileShortID
        }
      });
    }
  };
  const IS_PROFILE_CHANGED = useMemo(() => shortId !== profile.shortid || name !== profile.name || surname !== (profile.surname ?? '') || email !== profile.email || countrycode !== (profile.countrycode ?? undefined) || description !== profile.description, [profile, shortId, name, email, surname, countrycode, description]);
  const DISABLED_BTN = useMemo(() => error?.show || shortId.trim().length < minLength || shortId.trim().length > maxLength || !IS_PROFILE_CHANGED, [error?.show, shortId, IS_PROFILE_CHANGED]);
  return <>
            <div className={s.inputsBlock}>
                <div className={s.inputsBlock__main}>
                    <div>
                        <Input placeholder={tr({
            id: 'public.profile.username'
          })} name={'shortId'} onPrint={e => onChange('shortId', e)} value={shortId} description={{
            text: typeof error === 'undefined' ? tr({
              id: 'public.profile.symbols'
            }) : <div><SpriteIcon name={'warning'} width={20} height={20} /> {' '} {error.text}  </div>,
            error: typeof error !== 'undefined'
          }} label={<span className={s.inputsBlock__label}>
                                    {tr({
              id: 'settings.page.username'
            })}
                                    <Info isMobile={isMobile} id='username' tooltip={'public.profile.validation'} />
                                </span>} data-sentry-element="Input" data-sentry-source-file="InputsBlockLoaded.tsx" />
                    </div>
                    <Input name={'email'} disabled={true} onPrint={e => onChange('email', e)} value={email} placeholder={tr({
          id: 'public.profile.email'
        })} label={tr({
          id: 'settings.page.email'
        })} data-sentry-element="Input" data-sentry-source-file="InputsBlockLoaded.tsx" />
                    <Input className={s.inputsBlock__input} value={name} placeholder={tr({
          id: 'public.profile.name'
        })} name={'name'} onPrint={e => onChange('name', e)} label={tr({
          id: 'settings.page.name'
        })} data-sentry-element="Input" data-sentry-source-file="InputsBlockLoaded.tsx" />
                    <Input className={s.inputsBlock__input} placeholder={tr({
          id: 'public.profile.surname'
        })} value={surname} name={'surname'} onPrint={e => onChange('surname', e)} label={tr({
          id: 'settings.page.surname'
        })} data-sentry-element="Input" data-sentry-source-file="InputsBlockLoaded.tsx" />
                    <Select label={tr({
          id: 'settings.page.country'
        })} className={s.inputsBlock__select} options={countryList} name={'countrycode'} changeHandler={({
          value
        }) => {
          onChange('countrycode', value);
        }} defaultValue={countryList.find(item => item.value === profile.countrycode) ?? null} data-sentry-element="Select" data-sentry-source-file="InputsBlockLoaded.tsx" />

                    <Textarea label={tr({
          id: 'settings.page.bio'
        })} onPrint={value => onChange('description', value)} value={description ?? ''} placeholder={tr({
          id: 'settings.page.bio.placeholder'
        })} data-sentry-element="Textarea" data-sentry-source-file="InputsBlockLoaded.tsx" />
                </div>
                {/* <div>
                    <Divider />
                    <SocialsBlock />
                 </div> */}
                <Divider data-sentry-element="Divider" data-sentry-source-file="InputsBlockLoaded.tsx" />
                <Button id='delete_profile_settings_button' className={s.inputsBlock__delete} size='medium' color='Transparent-Red' onClick={openDeleteAccountModal} icons={{
        left: {
          name: 'trash'
        }
      }} data-sentry-element="Button" data-sentry-source-file="InputsBlockLoaded.tsx">
                    {tr({
          id: 'btn.delete.account'
        })}
                </Button>

                <Divider data-sentry-element="Divider" data-sentry-source-file="InputsBlockLoaded.tsx" />
                <div className={s.inputsBlock__footer}>
                    <Button id='edit_profile_button' size='large' color='Black' disabled={DISABLED_BTN} onClick={saveHandler} data-sentry-element="Button" data-sentry-source-file="InputsBlockLoaded.tsx">
                        {tr({
            id: 'btn.save'
          })}
                    </Button>
                    {IS_PROFILE_CHANGED && <Button id='default_button' size='large' color='White' onClick={setDefaultState}>
                            {tr({
            id: 'btn.cancel'
          })}
                        </Button>}
                </div>

                <Modal isOpen={isDeleteAccountModalOpen} onClose={closeDeleteAccountModal} data-sentry-element="Modal" data-sentry-source-file="InputsBlockLoaded.tsx">
                    <DeleteAccount closeHandler={closeDeleteAccountModal} deleteHandler={deleteAccountHandler} data-sentry-element="DeleteAccount" data-sentry-source-file="InputsBlockLoaded.tsx" />
                </Modal>
            </div>

        </>;
};